import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/articles';
import { articleImageUrls } from '@/mocks/imageUrls';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categoryId === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-heading text-foreground-950 mb-2">分类未找到</h2>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">返回首页</Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 成人视频指南`,
    description: category.description,
    url: `https://example.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://example.com/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <div className="mb-4">
              <Breadcrumb items={[{ label: category.name }]} />
            </div>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              {category.description}
            </p>
            <p className="text-xs text-foreground-400 mt-3">
              共 {categoryArticles.length} 篇文章
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:border-background-300/60 hover:text-foreground-900'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => {
                const imageUrl = articleImageUrls[article.slug] || 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20Japanese%20media%20guide%20on%20a%20clean%20desk%20with%20soft%20warm%20lighting%2C%20editorial%20photography%20style&width=800&height=500&seq=default&orientation=landscape';
                return (
                  <article key={article.id} className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden group hover:border-background-300/60 transition-all duration-300">
                    <Link to={`/article/${article.slug}`} className="block">
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    </Link>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">
                          {category.name}
                        </span>
                        <span className="text-xs text-foreground-500">{article.publishDate}</span>
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                      </div>
                      <Link to={`/article/${article.slug}`}>
                        <h3 className="text-base md:text-lg font-semibold font-heading text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                      </Link>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-500">{article.author}</span>
                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          {article.tags.slice(0, 2).map((tag: string) => (
                            <span key={tag} className="text-xs px-2 py-0.5 rounded bg-background-200/70 text-foreground-600 whitespace-nowrap">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}